import { leaveScopePath } from '../leave/leaveView'
import type { Resource } from './model'
import type { AttentionItem } from './useHomeAttention'

export interface Shortcut extends Resource {
  aliases: string[]
  actions: Resource[]
  attention?: { items: AttentionItem[]; count: number }
  temporary?: boolean
}

interface ShortcutGroup {
  paths: string[]
  label: string
  actions: { source: string; path?: string; label: string; detail: string }[]
}

const GROUPS: ShortcutGroup[] = [
  {
    paths: ['/leave/new', '/leave'], label: 'Leave',
    actions: [
      { source: '/leave/new', label: 'Request leave', detail: 'Start a new time-off request' },
      { source: '/leave', path: leaveScopePath('my'), label: 'My leave requests', detail: 'Track your requests and their status' },
    ],
  },
  {
    paths: ['/bills/new', '/bills'], label: 'Expenses',
    actions: [
      { source: '/bills/new', label: 'Submit an expense', detail: 'Add a bill, receipt or reimbursement' },
      { source: '/bills', path: '/my-profile?tab=bills', label: 'My expenses', detail: 'Track the bills you’ve submitted' },
    ],
  },
  {
    paths: ['/it-tickets'], label: 'IT help',
    actions: [
      { source: '/it-tickets', label: 'Report an issue', detail: 'Send a new request to the IT team' },
      { source: '/it-tickets', path: '/it-tickets?tab=previous', label: 'My IT tickets', detail: 'Check progress on your support requests' },
    ],
  },
  {
    paths: ['/handbook', '/policies', '/documents'], label: 'Knowledge',
    actions: [
      { source: '/handbook', label: 'Handbook', detail: 'Find a procedure or how-to guide' },
      { source: '/policies', label: 'Company policies', detail: 'Read workplace guidance and policies' },
      { source: '/documents', label: 'My documents', detail: 'Review and sign your documents' },
    ],
  },
  {
    paths: ['/cafe', '/cafe/history'], label: 'Cafe',
    actions: [
      { source: '/cafe', label: 'Place an order', detail: 'Order coffee and food from Duo Cafe' },
      { source: '/cafe/history', label: 'My past orders', detail: 'See your cafe order history' },
    ],
  },
  {
    paths: ['/my-profile'], label: 'My profile',
    actions: [
      { source: '/my-profile', label: 'My details', detail: 'Contact, bank and personal details' },
      { source: '/my-team', path: '/my-profile?tab=team', label: 'My team', detail: 'See your colleagues and reporting line' },
    ],
  },
]

// Build from the already permission-filtered catalogue. A menu must never
// restore an action that the employee cannot see in the main catalogue.
export function buildShortcutCatalogue(resources: Resource[], { groupKnowledge = true }: { groupKnowledge?: boolean } = {}): Shortcut[] {
  const byPath = new Map(resources.map(resource => [resource.path, resource]))
  const seen = new Set<ShortcutGroup>()
  return resources.flatMap(resource => {
    const group = GROUPS.find(item => item.paths.includes(resource.path) && (groupKnowledge || item.label !== 'Knowledge'))
    const single = { ...resource, aliases: [resource.path], actions: [resource] }
    if (!group) return [single]
    if (seen.has(group)) return []
    const actions = group.actions.flatMap(action => {
      const source = byPath.get(action.source)
      if (!source) return []
      const path = action.path || source.path
      return [{ ...source, id: path, path, label: action.label, detail: action.detail }]
    })
    if (actions.length < 2) return [single]
    seen.add(group)
    const members = group.paths.map(path => byPath.get(path)).filter((item): item is Resource => Boolean(item))
    return [{
      ...members[0], label: group.label,
      detail: actions.map(action => action.label).join(' · '),
      keywords: members.map(item => `${item.label} ${item.keywords || ''}`).join(' '),
      teamApp: members.some(item => item.teamApp),
      aliases: members.map(item => item.path), actions,
    }]
  })
}

// Existing pins retain their order; two older pins for the same topic become
// one expandable tile without overwriting the employee's saved preferences.
export function resolvePinnedShortcuts(paths: string[], catalogue: Shortcut[]): Shortcut[] {
  const seen = new Set<string>()
  return paths.flatMap(path => {
    const shortcut = catalogue.find(item => item.aliases.includes(path))
    if (!shortcut || seen.has(shortcut.path)) return []
    seen.add(shortcut.path)
    return [shortcut]
  })
}
